/**
 * Pure helpers for joining S3 storage usage onto the My Projects list.
 *
 * The /user-files/by-project endpoint groups files by the project's ObjectId
 * (group._id) and returns total_size / total_files per project. ProjectDashboard
 * loads projects separately, so we index the storage groups by id and annotate
 * each project with its usage.
 */

/**
 * Index by-project groups by their project id (string-keyed for safe lookup).
 *
 * @param {Array<{_id?: any, total_size?: number, total_files?: number}>} groups
 * @returns {Object<string, {total_size: number, total_files: number}>}
 */
export const indexStorageByProjectId = (groups) => {
  const list = Array.isArray(groups) ? groups : [];
  return list.reduce((map, g) => {
    if (g && g._id != null) {
      map[g._id.toString()] = {
        total_size: g.total_size || 0,
        total_files: g.total_files || 0,
      };
    }
    return map;
  }, {});
};

/**
 * Index by-observation groups by their observation id. Same shape as
 * indexStorageByProjectId — both endpoints key groups on `_id`.
 *
 * @param {Array<{_id?: any, total_size?: number, total_files?: number}>} groups
 * @returns {Object<string, {total_size: number, total_files: number}>}
 */
export const indexStorageByObservationId = indexStorageByProjectId;

/**
 * Attach storage_bytes / file_count to each project from the by-project groups,
 * and compute the grand total across all projects.
 *
 * @param {Array<{_id?: any}>} projects
 * @param {Array<{_id?: any, total_size?: number, total_files?: number}>} byProjectGroups
 * @returns {{projects: Array, totalStorageBytes: number}}
 */
export const annotateProjectsWithStorage = (projects, byProjectGroups) => {
  const list = Array.isArray(projects) ? projects : [];
  const index = indexStorageByProjectId(byProjectGroups);
  const annotated = list.map((project) => {
    const usage = (project && project._id != null && index[project._id.toString()]) || null;
    return {
      ...project,
      storage_bytes: usage ? usage.total_size : 0,
      file_count: usage ? usage.total_files : 0,
    };
  });
  const totalStorageBytes = annotated.reduce((sum, p) => sum + (p.storage_bytes || 0), 0);
  return { projects: annotated, totalStorageBytes };
};
